import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Mail, Phone, MapPin, PawPrint, ExternalLink, FileText, ClipboardList, DollarSign } from 'lucide-react';
import WebsiteSections from '@/components/website/SectionRenderer';

const PublicWebsite = () => {
  const { subdomain } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [foundAnimals, setFoundAnimals] = useState([]);
  const [events, setEvents] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('home');

  const fetchWebsite = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/public/website/${subdomain}`);
      setWebsite(response.data);
      
      // Fetch adoptable pets if shelter
      if (response.data.user_role === 'shelter') {
        try {
          const petsRes = await axios.get(`/public/website/${subdomain}/adoptable-pets`);
          setAnimals(petsRes.data);
        } catch (e) {
          console.log('No adoptable pets available');
        }
        
        // Fetch found animals
        try {
          const foundRes = await axios.get(`/public/website/${subdomain}/found-pets`);
          setFoundAnimals(foundRes.data || []);
        } catch (e) {
          console.log('No found pets available');
        }
        
        // Fetch events
        try {
          const eventsRes = await axios.get(`/public/website/${subdomain}/events`);
          setEvents(eventsRes.data || []);
        } catch (e) {
          console.log('No events available');
        }
      }
      
      // Fetch published forms for all roles
      try {
        const formsRes = await axios.get(`/public/website/${subdomain}/forms`);
        setForms(formsRes.data || []);
      } catch (e) {
        console.log('No forms available');
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Website not found');
    } finally {
      setLoading(false);
    }
  }, [subdomain]);

  useEffect(() => {
    fetchWebsite();
  }, [fetchWebsite]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <PawPrint className="w-16 h-16 mx-auto animate-pulse text-primary" />
          <p className="mt-4 text-stone-600">Loading website...</p>
        </div>
      </div>
    );
  }

  if (error || !website) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-stone-800">404</h1>
          <p className="mt-2 text-stone-600">{error || 'Website not found'}</p>
          <Button className="mt-4" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const theme = website.theme || {};
  const primaryColor = theme.primaryColor || '#166534';
  const secondaryColor = theme.secondaryColor || '#f97316';
  const backgroundColor = theme.backgroundColor || '#fdfbf7';
  const textColor = theme.textColor || '#1c1917';
  const fontFamily = theme.font_family || 'system-ui';

  return (
    <div style={{ backgroundColor, color: textColor, fontFamily }} className="min-h-screen">
      {/* Header / Navigation */}
      <header style={{ backgroundColor: primaryColor }} className="text-white py-4 px-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            {website.logo_url && (
              <img src={website.logo_url} alt="Logo" className="h-12 w-auto object-contain" />
            )}
            <div>
              <h1 className="text-xl font-bold">{website.title || website.subdomain}</h1>
              {website.tagline && <p className="text-sm opacity-90">{website.tagline}</p>}
            </div>
          </div>
          <nav className="flex gap-4">
            <button 
              onClick={() => setActiveTab('home')}
              className={`hover:underline ${activeTab === 'home' ? 'underline font-semibold' : ''}`}
            >
              Home
            </button>
            <button 
              onClick={() => setActiveTab('adopt')}
              className={`hover:underline ${activeTab === 'adopt' ? 'underline font-semibold' : ''}`}
            >
              Pets for Adoption
            </button>
            <button 
              onClick={() => setActiveTab('found')}
              className={`hover:underline ${activeTab === 'found' ? 'underline font-semibold' : ''}`}
            >
              Found Pets
            </button>
            {forms.length > 0 && (
              <button 
                onClick={() => setActiveTab('forms')}
                className={`hover:underline ${activeTab === 'forms' ? 'underline font-semibold' : ''}`}
              >
                Forms
              </button>
            )}
            <button 
              onClick={() => setActiveTab('events')}
              className={`hover:underline ${activeTab === 'events' ? 'underline font-semibold' : ''}`}
            >
              Events
            </button>
            <button 
              onClick={() => setActiveTab('contact')}
              className={`hover:underline ${activeTab === 'contact' ? 'underline font-semibold' : ''}`}
            >
              Contact
            </button>
          </nav>
        </div>
      </header>

      {/* Home Tab - Render Custom Sections or Default */}
      {activeTab === 'home' && (
        <>
          {/* If custom sections exist, render them */}
          {website.sections && website.sections.length > 0 ? (
            <WebsiteSections 
              sections={website.sections} 
              primaryColor={primaryColor} 
              secondaryColor={secondaryColor}
            />
          ) : (
            <>
              {/* Default Hero Section */}
              <div 
                className="relative py-24 text-center text-white"
                style={{ 
                  backgroundImage: website.hero_image_url ? `url(${website.hero_image_url})` : `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative z-10 container mx-auto px-6">
                  <h2 className="text-4xl sm:text-5xl font-bold mb-4">{website.title}</h2>
                  <p className="text-xl mb-8 max-w-2xl mx-auto">{website.tagline || website.description}</p>
                  <Button 
                    size="lg" 
                    className="text-white"
                    style={{ backgroundColor: secondaryColor }}
                    onClick={() => setActiveTab('adopt')}
                  >
                    View Our Pets
                  </Button>
                </div>
              </div>

              {/* Default About Section */}
              {website.description && (
                <div className="container mx-auto px-6 py-12">
                  <h3 className="text-2xl font-bold mb-4 text-center" style={{ color: primaryColor }}>About Us</h3>
                  <p className="text-lg text-center max-w-3xl mx-auto">{website.description}</p>
                </div>
              )}
            </>
          )}

          {/* Featured Pets - Always show if available */}
          {animals.length > 0 && (
            <div className="container mx-auto px-6 py-12 bg-stone-50">
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: primaryColor }}>
                Featured Pets
              </h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {animals.slice(0, 4).map(animal => (
                  <Card key={animal.animal_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-square bg-stone-100 flex items-center justify-center">
                      {animal.photos?.length > 0 ? (
                        <img src={animal.photos[0].url || animal.photos[0]} alt={animal.name} className="w-full h-full object-cover" />
                      ) : (
                        <PawPrint className="w-16 h-16 text-stone-300" />
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-semibold">{animal.name}</h4>
                      <p className="text-sm text-stone-600">{animal.species} • {animal.breed}</p>
                      {animal.adoption_fee > 0 && (
                        <p className="text-sm font-medium mt-2" style={{ color: primaryColor }}>
                          ${animal.adoption_fee}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              {animals.length > 4 && (
                <div className="text-center mt-6">
                  <Button variant="outline" onClick={() => setActiveTab('adopt')}>
                    View All Pets ({animals.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Adoption Tab */}
      {activeTab === 'adopt' && (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
            Pets for Adoption
          </h2>
          {animals.length > 0 ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {animals.map(animal => (
                <Card key={animal.animal_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-square bg-stone-100 flex items-center justify-center">
                    {animal.photos?.length > 0 ? (
                      <img src={animal.photos[0].url || animal.photos[0]} alt={animal.name} className="w-full h-full object-cover" />
                    ) : (
                      <PawPrint className="w-16 h-16 text-stone-300" />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg">{animal.name}</h4>
                    <p className="text-sm text-stone-600">{animal.species} • {animal.breed}</p>
                    <p className="text-sm text-stone-500">{animal.gender} • {animal.color}</p>
                    {animal.temperament && (
                      <p className="text-sm mt-2 italic">{animal.temperament}</p>
                    )}
                    {animal.adoption_fee > 0 && (
                      <p className="text-lg font-bold mt-2" style={{ color: primaryColor }}>
                        ${animal.adoption_fee}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PawPrint className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No pets currently available for adoption</p>
            </div>
          )}
        </div>
      )}

      {/* Found Pets Tab */}
      {activeTab === 'found' && (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-bold mb-4 text-center" style={{ color: primaryColor }}>
            Found Pets
          </h2>
          <p className="text-center text-stone-600 mb-8 max-w-2xl mx-auto">
            These pets were found in our area. If you recognize any of them or have lost a pet, please contact us.
          </p>
          {foundAnimals.length > 0 ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {foundAnimals.map(animal => (
                <Card key={animal.animal_id} className="overflow-hidden hover:shadow-lg transition-shadow border-amber-200">
                  <div className="aspect-square bg-stone-100 flex items-center justify-center relative">
                    {animal.photos?.length > 0 ? (
                      <img src={animal.photos[0].url || animal.photos[0]} alt={animal.name || 'Found pet'} className="w-full h-full object-cover" />
                    ) : (
                      <PawPrint className="w-16 h-16 text-stone-300" />
                    )}
                    <Badge className="absolute top-2 right-2 bg-amber-500">Found Pet</Badge>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg">{animal.name || 'Unknown'}</h4>
                    <p className="text-sm text-stone-600">{animal.species} • {animal.breed || 'Unknown breed'}</p>
                    <p className="text-sm text-stone-500">{animal.gender || 'Unknown'} • {animal.color || 'Unknown color'}</p>
                    {animal.website_publish_date && (
                      <p className="text-xs text-stone-400 mt-2">
                        Found: {new Date(animal.website_publish_date).toLocaleDateString()}
                      </p>
                    )}
                    {animal.website_description && (
                      <p className="text-sm mt-2 italic text-stone-600">{animal.website_description}</p>
                    )}
                    {animal.microchip_number && (
                      <p className="text-xs mt-2 bg-stone-100 px-2 py-1 rounded">
                        Microchip: {animal.microchip_number}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PawPrint className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No found pets currently listed</p>
              <p className="text-sm text-stone-400 mt-2">Check back later or contact us if you&apos;ve lost a pet</p>
            </div>
          )}
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
            Upcoming Events
          </h2>
          {events.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => (
                <Card key={event.event_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {event.image_url && (
                    <img src={event.image_url} alt={event.title} className="w-full h-48 object-cover" />
                  )}
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-lg">{event.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-stone-500 mt-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(event.event_date).toLocaleDateString()}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-stone-500 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.description && (
                      <p className="text-sm mt-2">{event.description}</p>
                    )}
                    {event.registration_url && (
                      <a 
                        href={event.registration_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm mt-3"
                        style={{ color: primaryColor }}
                      >
                        Register <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No upcoming events</p>
            </div>
          )}
        </div>
      )}

      {/* Forms Tab */}
      {activeTab === 'forms' && (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
            Available Forms
          </h2>
          {forms.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forms.map(form => (
                <Card key={form.template_id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/forms/${form.template_id}`)}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}15` }}
                      >
                        <ClipboardList className="w-6 h-6" style={{ color: primaryColor }} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{form.title}</h4>
                        <Badge variant="secondary" className="text-xs mt-1 capitalize">
                          {form.form_type?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    {form.description && (
                      <p className="text-sm text-stone-600 mt-4 line-clamp-2">{form.description}</p>
                    )}
                    {form.payment_enabled && form.payment_amount > 0 && (
                      <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-sm font-medium">${form.payment_amount} payment required</span>
                      </div>
                    )}
                    <Button 
                      className="w-full mt-4" 
                      style={{ backgroundColor: secondaryColor }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/forms/${form.template_id}`); }}
                    >
                      <FileText className="w-4 h-4 mr-2" /> Open Form
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">No forms available at this time</p>
            </div>
          )}
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
            Contact Us
          </h2>
          <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md p-8">
            {website.contact_email && (
              <div className="flex items-center gap-4 mb-4">
                <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                <a href={`mailto:${website.contact_email}`} className="hover:underline">
                  {website.contact_email}
                </a>
              </div>
            )}
            {website.contact_phone && (
              <div className="flex items-center gap-4 mb-4">
                <Phone className="w-5 h-5" style={{ color: primaryColor }} />
                <a href={`tel:${website.contact_phone}`} className="hover:underline">
                  {website.contact_phone}
                </a>
              </div>
            )}
            {website.address && (
              <div className="flex items-center gap-4 mb-4">
                <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                <span>{website.address}</span>
              </div>
            )}
            
            {website.footer?.social_links && Object.keys(website.footer.social_links).length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3">Follow Us</h4>
                <div className="flex gap-3">
                  {website.footer.social_links.facebook && (
                    <a href={website.footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                      Facebook
                    </a>
                  )}
                  {website.footer.social_links.instagram && (
                    <a href={website.footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                      Instagram
                    </a>
                  )}
                  {website.footer.social_links.twitter && (
                    <a href={website.footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                      Twitter
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ backgroundColor: primaryColor }} className="text-white py-8 mt-auto">
        <div className="container mx-auto px-6 text-center">
          <p>{website.footer?.copyright_text || `© ${new Date().getFullYear()} ${website.title}. All rights reserved.`}</p>
          <p className="text-sm mt-2 opacity-75">
            Powered by International Pet Registry
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicWebsite;
